// Modal primitive (Phase 0, design-system audit §7; critique X2/a11y). One implementation
// of: overlay + role=dialog + focus trap + Escape-to-close + scroll-lock + restore-focus.
// Replaces the ad-hoc per-page modals and the native window.confirm()/prompt() (via confirm.jsx).
// Reuses the existing .modal-overlay/.modal/.modal__head/.modal__body classes.
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { lockScroll, unlockScroll } from "../fullscreen.jsx";   // refcounted body scroll-lock (shared across overlays)

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, children, footer, wide = false }) {
  const ref = useRef(null);
  // Hold the latest onClose in a ref so the focus effect can depend on `open` ALONE. Depending on
  // onClose (an inline arrow recreated every render) made the effect re-run on every keystroke and
  // re-grab focus to the first element — defocusing a typed input after one character.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const node = ref.current;
    const prevFocus = document.activeElement;
    lockScroll(); // existing scroll-lock class, refcounted (two overlays can't unlock each other)

    const list = () => Array.from(node?.querySelectorAll(FOCUSABLE) || []);
    // move focus into the dialog ONCE on open — prefer a text input (so prompts are typable
    // immediately), else the first focusable (typically the ✕).
    (node?.querySelector("input:not([type=checkbox]):not([type=radio]),textarea") || list()[0] || node)?.focus?.();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
      } else if (e.key === "Tab") {
        const f = list();
        if (!f.length) return;
        const i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) {
          e.preventDefault();
          f[f.length - 1].focus();
        } else if (!e.shiftKey && i === f.length - 1) {
          e.preventDefault();
          f[0].focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      unlockScroll();
      prevFocus?.focus?.();
    };
  }, [open]);   // open ONLY — not onClose (re-running this on every keystroke stole input focus)

  if (!open) return null;

  // Portal to <body> so an ancestor transform/animation (e.g. `.main`'s ccFade) can't become the
  // containing block and clip this fixed overlay.
  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className={"modal" + (wide ? " modal--wide" : "")}
        role="dialog"
        aria-modal="true"
        aria-label={title || undefined}
        ref={ref}
        tabIndex={-1}
      >
        {title && (
          <div className="modal__head">
            <h3>{title}</h3>
            <button className="iconbtn" aria-label="Close" onClick={onClose}>
              ✕
            </button>
          </div>
        )}
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
